import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { socialApi } from "../api/socialApi";
import { useSocialConnections } from "../hooks/useSocialSync";
import { SocialConnection, SocialPlatform } from "../types/social.types";

const platforms: Array<{
  key: SocialPlatform;
  label: string;
  description: string;
  productionNote?: string;
}> = [
  {
    key: "facebook",
    label: "Facebook / Meta",
    description: "Connect Pages, catalog and Facebook posting.",
  },
  {
    key: "instagram",
    label: "Instagram Business",
    description: "Publish image posts and product content where allowed.",
  },
  {
    key: "whatsapp",
    label: "WhatsApp Business",
    description:
      "Connect WABA, phone number, templates and messaging capability.",
    productionNote: "Production messaging waits for Meta App Review approval.",
  },
  {
    key: "tiktok",
    label: "TikTok",
    description: "Prepare and publish/export content through TikTok permissions.",
  },
  {
    key: "google",
    label: "Google / Merchant",
    description: "Connect Google profile now, then enable Merchant sync when verified.",
  },
];

const statusColorScheme: Record<string, string> = {
  connected: "green",
  production_ready: "green",
  testing: "blue",
  pending_review: "orange",
  expired: "yellow",
  revoked: "red",
  error: "red",
  not_connected: "gray",
};

const statusLabel: Record<string, string> = {
  connected: "Connected",
  production_ready: "Production ready",
  testing: "Testing",
  pending_review: "Pending review",
  expired: "Expired",
  revoked: "Revoked",
  error: "Error",
  not_connected: "Not connected",
};

const ConnectionSettingsForm = ({
  connection,
  onSave,
  loading,
}: {
  connection: SocialConnection;
  onSave: (id: string, values: Partial<SocialConnection>) => Promise<any>;
  loading: boolean;
}) => {
  const toast = useToast();
  const [values, setValues] = useState({
    pageId: connection.pageId || "",
    pageName: connection.pageName || "",
    igBusinessId: connection.igBusinessId || "",
    productCatalogId: connection.productCatalogId || "",
    googleMerchantId:
      connection.googleMerchantId || connection.merchantId || "",
    googleDataSourceName: connection.googleDataSourceName || "",
    wabaId: connection.wabaId || "",
    phoneNumberId: connection.phoneNumberId || "",
    displayPhoneNumber: connection.displayPhoneNumber || "",
    verifiedName: connection.verifiedName || "",
  });

  const inputBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");

  const connectionId = connection._id || connection.id || "";

  const save = async () => {
    try {
      await onSave(connectionId, values);

      toast({
        title: "Connection settings saved",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: err?.message || "Failed to save settings",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  if (!connectionId) return null;

  return (
    <VStack align="stretch" spacing={3} mt={4}>
      {(connection.platform === "facebook" ||
        connection.platform === "instagram") && (
        <>
          <FormControl>
            <FormLabel fontSize="xs">Facebook Page ID</FormLabel>
            <Input
              size="sm"
              value={values.pageId}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  pageId: event.target.value,
                }))
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs">Instagram Business ID</FormLabel>
            <Input
              size="sm"
              value={values.igBusinessId}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  igBusinessId: event.target.value,
                }))
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs">Meta Product Catalog ID</FormLabel>
            <Input
              size="sm"
              value={values.productCatalogId}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  productCatalogId: event.target.value,
                }))
              }
            />
          </FormControl>
        </>
      )}

      {connection.platform === "google" && (
        <>
          <FormControl>
            <FormLabel fontSize="xs">Google Merchant ID</FormLabel>
            <Input
              size="sm"
              value={values.googleMerchantId}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  googleMerchantId: event.target.value,
                }))
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs">Google Data Source Name / ID</FormLabel>
            <Input
              size="sm"
              value={values.googleDataSourceName}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  googleDataSourceName: event.target.value,
                }))
              }
            />
          </FormControl>
        </>
      )}

      {connection.platform === "whatsapp" && (
        <>
          <FormControl>
            <FormLabel fontSize="xs">WABA ID</FormLabel>
            <Input
              size="sm"
              value={values.wabaId}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, wabaId: event.target.value }))
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs">Phone Number ID</FormLabel>
            <Input
              size="sm"
              value={values.phoneNumberId}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  phoneNumberId: event.target.value,
                }))
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs">Display Phone</FormLabel>
            <Input
              size="sm"
              value={values.displayPhoneNumber}
              bg={inputBg}
              color={textColor}
              borderColor={borderColor}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  displayPhoneNumber: event.target.value,
                }))
              }
            />
          </FormControl>
        </>
      )}

      <Button
        size="sm"
        alignSelf="flex-start"
        colorScheme="teal"
        onClick={save}
        isLoading={loading}
      >
        Save settings
      </Button>
    </VStack>
  );
};

export const SocialConnectCenter: React.FC = () => {
  const { connections, loading, error, reload, updateConnection } =
    useSocialConnections();
  const [openSettings, setOpenSettings] = useState<Record<string, boolean>>({});

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "whiteAlpha.900");

  const getConnection = (platform: SocialPlatform) => {
    if (platform === "instagram") {
      return connections.find(
        (item) => item.platform === "instagram" || item.igBusinessId,
      );
    }

    return connections.find((item) => item.platform === platform);
  };

  const connect = (platform: SocialPlatform) => {
    window.location.href = socialApi.getConnectUrl(platform);
  };

  return (
    <VStack align="stretch" spacing={5}>
      <HStack justify="space-between" align="start">
        <Box>
          <Text fontSize="xl" fontWeight="bold" color={headingColor}>
            Connect Social Shop
          </Text>
          <Text color={mutedText}>
            Connect once, then PickDengu can sync approved listings on your
            behalf.
          </Text>
        </Box>

        <Button variant="outline" onClick={reload} isLoading={loading}>
          Refresh
        </Button>
      </HStack>

      {error && <Text color="red.500">{error}</Text>}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {platforms.map((platform) => {
          const connection = getConnection(platform.key);
          const status = connection?.status || "not_connected";
          const connected = status !== "not_connected";
          const connectionId = connection?._id || connection?.id || platform.key;

          return (
            <Box
              key={platform.key}
              border="1px solid"
              borderColor={borderColor}
              rounded="2xl"
              p={5}
              bg={cardBg}
              shadow="sm"
            >
              <HStack justify="space-between" align="start">
                <Box minW={0}>
                  <Text fontWeight="bold" color={headingColor}>
                    {platform.label}
                  </Text>
                  <Text fontSize="sm" color={mutedText} mt={1}>
                    {platform.description}
                  </Text>
                </Box>

                <Badge colorScheme={statusColorScheme[status] || "gray"}>
                  {statusLabel[status] || status}
                </Badge>
              </HStack>

              {platform.productionNote && (
                <Text fontSize="xs" color={mutedText} mt={3}>
                  {platform.productionNote}
                </Text>
              )}

              {connection && (
                <Text fontSize="xs" color={mutedText} mt={3} noOfLines={2}>
                  {connection.accountName ||
                    connection.pageName ||
                    connection.pageId ||
                    connection.igBusinessId ||
                    connection.displayPhoneNumber ||
                    connection.googleMerchantId ||
                    connection.tiktokOpenId ||
                    "Connected account"}
                </Text>
              )}

              <HStack mt={4} spacing={2} wrap="wrap">
                <Button
                  colorScheme={connected ? "gray" : "teal"}
                  variant={connected ? "outline" : "solid"}
                  onClick={() => connect(platform.key)}
                >
                  {connected ? "Reconnect" : `Connect ${platform.label}`}
                </Button>

                {connection && (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      setOpenSettings((prev) => ({
                        ...prev,
                        [connectionId]: !prev[connectionId],
                      }))
                    }
                  >
                    Settings
                  </Button>
                )}
              </HStack>

              {connection && (
                <Collapse in={!!openSettings[connectionId]} animateOpacity>
                  <ConnectionSettingsForm
                    connection={connection}
                    loading={loading}
                    onSave={updateConnection}
                  />
                </Collapse>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </VStack>
  );
};
